import React from 'react';

interface LessonPreviewProps {
  onClick?: () => void;
}

const LessonPreview: React.FC<LessonPreviewProps> = ({ onClick }) => {
  const gridSize = 160;
  const padding = 20;
  const range = 5;
  const cellSize = (gridSize - padding * 2) / (range * 2);
  const center = gridSize / 2;

  // Convert grid coordinates to pixel positions
  const toPixel = (gridX: number, gridY: number) => ({
    x: center + gridX * cellSize,
    y: center - gridY * cellSize,
  });

  // Example points from the lesson
  const point1 = toPixel(2, 1);
  const point2 = toPixel(-1, 3);

  return (
    <div
      className="relative bg-white rounded-xl shadow-lg cursor-pointer transition-transform hover:scale-105 overflow-hidden"
      style={{ width: gridSize + 40, height: gridSize + 80 }}
      onClick={onClick}
    >
      {/* Header bar */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-gray-300" />
          <div className="h-1.5 w-16 bg-emerald-400 rounded-full" />
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[8px] font-bold text-orange-400">3</span>
          <svg className="w-3 h-3 text-orange-400" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 23c-3.866 0-7-3.134-7-7 0-2.551 1.369-4.787 3.455-6.015C8.18 9.793 8 9.418 8 9c0-.552.448-1 1-1 .379 0 .725.214.894.553C10.553 7.612 11.25 6.5 12 5.5c.75 1 1.447 2.112 2.106 3.053.169-.339.515-.553.894-.553.552 0 1 .448 1 1 0 .418-.18.793-.455.985C17.631 11.213 19 13.449 19 16c0 3.866-3.134 7-7 7z"/>
          </svg>
        </div>
      </div>

      {/* Question text */}
      <p className="text-[7px] text-gray-600 text-center px-2 py-1.5 leading-tight">
        Place a point at coordinates (2, 1)
      </p>

      {/* Mini grid */}
      <div className="flex justify-center pb-2">
        <svg
          width={gridSize}
          height={gridSize}
          className="bg-slate-50"
        >
          {/* Grid lines */}
          {Array.from({ length: range * 2 + 1 }).map((_, i) => {
            const pos = padding + i * cellSize;
            const isCenter = i === range;
            return (
              <g key={i}>
                {/* Vertical */}
                <line
                  x1={pos}
                  y1={padding}
                  x2={pos}
                  y2={gridSize - padding}
                  stroke={isCenter ? '#94a3b8' : '#e2e8f0'}
                  strokeWidth={isCenter ? 1.5 : 0.5}
                />
                {/* Horizontal */}
                <line
                  x1={padding}
                  y1={pos}
                  x2={gridSize - padding}
                  y2={pos}
                  stroke={isCenter ? '#94a3b8' : '#e2e8f0'}
                  strokeWidth={isCenter ? 1.5 : 0.5}
                />
              </g>
            );
          })}

          {/* Point 1 - correctly placed */}
          <circle
            cx={point1.x}
            cy={point1.y}
            r={6}
            fill="#10b981"
            stroke="white"
            strokeWidth={1.5}
          />
          <text
            x={point1.x + 10}
            y={point1.y - 8}
            fontSize="7"
            fill="#374151"
            fontWeight="600"
          >
            (2, 1)
          </text>

          {/* Point 2 - pending placement indicator */}
          <circle
            cx={point2.x}
            cy={point2.y}
            r={6}
            fill="#3b82f6"
            stroke="white"
            strokeWidth={1.5}
            opacity={0.5}
          />
        </svg>
      </div>

      {/* Footer with button */}
      <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-emerald-50 border-t border-emerald-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <span className="text-[8px]">🎉</span>
            <span className="text-[8px] font-bold text-emerald-600">Correct!</span>
            <span className="text-[7px] text-emerald-500 ml-1">+15 XP</span>
          </div>
          <div className="px-2 py-0.5 bg-emerald-500 rounded text-[7px] font-bold text-white">
            Continue
          </div>
        </div>
      </div>

      {/* Play overlay hint */}
      <div className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/10 transition-colors group">
        <div className="w-12 h-12 rounded-full bg-white/90 shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <svg className="w-6 h-6 text-blue-500 ml-1" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </div>
      </div>
    </div>
  );
};

export default LessonPreview;
